import type { CuponDelCatalogo, NuevaReglaDeComision, NuevoCupon, NuevoPrecio, PrecioDelCatalogo, ProductoDelCatalogo, ReglaDeComision } from '../../api/ops/backoffice';

/**
 * Lógica sin estilos de **Catálogo y precios** (1.21, prototipo `CAT` y `COUPONS0`): los productos por grupos (Base,
 * Bloques, Paquetes de WhatsApp, Comisión de plataforma y Pausa), el precio con su «/mes», el precio programado (G185),
 * las fechas de «Desde», los cupones con su línea y la validación de «Nuevo cupón».
 */
export type Apartado = 'precios' | 'cupones';

export function apartadoDe(busqueda: URLSearchParams): Apartado {
    return busqueda.get('apartado') === 'cupones' ? 'cupones' : 'precios';
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const dos = (n: number) => String(n).padStart(2, '0');

/** «49 €», «146,25 €»: sin decimales si son céntimos redondos. */
export function euros(centimos: number): string {
    const e = centimos / 100;
    return `${e.toLocaleString('es-ES', { minimumFractionDigits: Number.isInteger(e) ? 0 : 2, maximumFractionDigits: 2 })} €`;
}

/** Precio de la fila: «49 €/mes» o, en un pago único (paquetes), «49 €». Sin precio, «—». */
export function textoPrecio(precio: PrecioDelCatalogo | null | undefined): string {
    if (precio === null || precio === undefined) {
        return '—';
    }
    return `${euros(precio.unit.amount)}${precio.interval === 'month' ? '/mes' : ''}`;
}

/** Día, mes (0-11) y año de una fecha en la hora de Madrid: las fechas del catálogo valen desde las 00:00 de Madrid. */
function enMadrid(d: Date): { dia: number; mes: number; anio: number } {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    const v = (tipo: string): number => Number(p.find((x) => x.type === tipo)?.value ?? '0');
    return { dia: v('day'), mes: v('month') - 1, anio: v('year') };
}

/** «1 de enero de 2027». */
export function fechaLarga(d: Date): string {
    const m = enMadrid(d);
    return `${String(m.dia)} de ${MESES[m.mes] ?? ''} de ${String(m.anio)}`;
}

/** Chip del precio programado (G185): «42 € desde el 1 de enero». */
export function textoProgramado(precio: PrecioDelCatalogo): string {
    const desde = precio.effective_from === null || precio.effective_from === undefined ? null : new Date(precio.effective_from);
    const cuando = desde === null ? '' : ` desde el ${String(enMadrid(desde).dia)} de ${MESES[enMadrid(desde).mes] ?? ''}`;
    return `${euros(precio.unit.amount).replace(' €', '')} €${cuando}`;
}

/** Si el precio aún no vale (programado para más adelante). */
export function esProgramado(precio: PrecioDelCatalogo | null | undefined, ahora: Date): boolean {
    return typeof precio?.effective_from === 'string' && Date.parse(precio.effective_from) > ahora.getTime();
}

/** Las fechas de «Desde» (prototipo `PRICE_DATES`): el día 1 de los tres meses siguientes; se propone el último. */
export function fechasDesde(ahora: Date): Date[] {
    return [1, 2, 3].map((n) => new Date(ahora.getFullYear(), ahora.getMonth() + n, 1));
}

/** Línea gris de un producto: lo que incluye su cupo, si lo tiene; si no, «—». */
export function lineaProducto(p: ProductoDelCatalogo): string {
    const q = p.quota;
    const n = q?.included;
    if (q === null || q === undefined || n === undefined) {
        return '—';
    }
    const cifra = n.toLocaleString('es-ES', { useGrouping: true }).replace(/(\d)(?=(\d{3})+$)/, '$1.');
    const porMes: Partial<Record<NonNullable<typeof q.concept>, string>> = {
        conversations: `${cifra} conversaciones al mes`,
        sends: `${cifra} envíos al mes`,
        signatures: `${cifra} firmas al mes`,
        voice_minutes: `${cifra} minutos de voz al mes`,
    };
    return (q.concept === undefined ? undefined : porMes[q.concept]) ?? '—';
}

/** Unidad del cupo de cada concepto con cupo de prueba (prototipo `TRIAL_Q`: conversaciones, envíos, firmas). */
const UNIDAD_PRUEBA: Partial<Record<NonNullable<NonNullable<ProductoDelCatalogo['quota']>['concept']>, string>> = {
    conversations: 'conversaciones',
    sends: 'envíos',
    signatures: 'firmas',
    voice_minutes: 'minutos de voz',
};

/**
 * «Cupo en la prueba» de un producto (G998, G1036; `quota.trial_included`): solo los bloques con cupo (los paquetes de
 * WhatsApp no, ni Reservas y Citas, que no tienen cupo, G1050). Null si el producto no lo lleva; `cifra` null = sin cifra.
 */
export function cupoDePrueba(p: ProductoDelCatalogo): { unidad: string; cifra: number | null; texto: string } | null {
    const concepto = p.quota?.concept;
    const unidad = concepto === undefined ? undefined : UNIDAD_PRUEBA[concepto];
    if (unidad === undefined || (p.kind !== 'block' && p.kind !== 'quantity')) {
        return null;
    }
    const cifra = p.quota?.trial_included ?? null;
    return { unidad, cifra, texto: cifra === null ? 'sin cifra · se cuenta sin cortar' : `${cifra.toLocaleString('es-ES', { useGrouping: true }).replace(/(\d)(?=(\d{3})+$)/, '$1.')} ${unidad}` };
}

/** La ventana «Cupo en la prueba · Asistente IA» (prototipo `v.tquota`). */
export function textoVentanaCupoPrueba(p: ProductoDelCatalogo, unidad: string): { titulo: string; texto: string; etiqueta: string; ayuda: string } {
    return {
        titulo: `Cupo en la prueba · ${p.name}`,
        texto: 'La prueba de 14 días abre la base y todos los bloques con este cupo. Vale para las pruebas que empiecen desde hoy.',
        etiqueta: `${unidad.charAt(0).toUpperCase()}${unidad.slice(1)} durante la prueba`,
        ayuda: 'Déjalo vacío para no poner cifra: durante la prueba se cuenta sin cortar.',
    };
}

export const ERROR_CUPO_PRUEBA = 'Escribe una cifra, o déjalo vacío.';

/** Lo escrito en «Cupo en la prueba»: vacío = sin cifra (null); una cifra entera de 0 en adelante; si no, `undefined` (error). */
export function cupoEscrito(valor: string): number | null | undefined {
    const t = valor.trim();
    if (t === '') {
        return null;
    }
    return /^\d{1,9}$/.test(t) ? Number(t) : undefined;
}

export interface FilaCatalogo {
    clave: string;
    nombre: string;
    linea: string;
    precio: string;
    /** El producto, para «Cambiar precio»; null en la comisión de plataforma. */
    producto: ProductoDelCatalogo | null;
    /** La regla vigente de la comisión de plataforma, para su «Cambiar precio» (G1136); null en los productos. */
    regla: ReglaDeComision | null;
    /** Chip de la comisión programada («1 € + 2 % desde el 1 de enero»), si hay una regla que aún no vale. */
    programada: string | null;
}

export interface GrupoCatalogo {
    titulo: string;
    nota: string;
    filas: FilaCatalogo[];
}

const GRUPOS: readonly { titulo: string; nota: string; tipos: readonly ProductoDelCatalogo['kind'][] }[] = [
    { titulo: 'Base', nota: 'La paga todo negocio', tipos: ['base'] },
    { titulo: 'Bloques', nota: 'Se suman a la base', tipos: ['block', 'quantity'] },
    { titulo: 'Paquetes de WhatsApp', nota: 'Pago único, sin IVA', tipos: ['one_time', 'topup'] },
];
const PAUSA = { titulo: 'Pausa', nota: 'Sustituye a todas las cuotas', tipos: ['pause'] as const };

/** Regla de comisión vigente de cada producto y concepto (la más reciente que ya vale y no ha terminado). */
export function reglasVigentes(reglas: readonly ReglaDeComision[], ahora: Date): ReglaDeComision[] {
    const vale = (r: ReglaDeComision) => Date.parse(r.valid_from) <= ahora.getTime() && (r.valid_to === null || r.valid_to === undefined || Date.parse(r.valid_to) > ahora.getTime());
    const por = new Map<string, ReglaDeComision>();
    for (const r of reglas.filter(vale)) {
        const clave = `${r.product_code}|${r.concept}`;
        const otra = por.get(clave);
        if (otra === undefined || Date.parse(r.valid_from) > Date.parse(otra.valid_from)) {
            por.set(clave, r);
        }
    }
    return [...por.values()];
}

const NOMBRE_COMISION: Readonly<Record<ReglaDeComision['concept'], string>> = { ticket_fee: 'Entradas', deposit: 'Señales', no_show: 'No-shows', late_cancellation: 'Cancelaciones tardías' };

/** «1 € + 1,5 % por entrada vendida online». */
export function lineaComision(r: ReglaDeComision): string {
    const partes = [r.fixed === null || r.fixed === undefined || r.fixed.amount === 0 ? null : euros(r.fixed.amount), r.percent_bp === 0 ? null : `${(r.percent_bp / 100).toLocaleString('es-ES')} %`].filter((x) => x !== null);
    const de = r.concept === 'ticket_fee' ? ' por entrada vendida online' : '';
    return partes.length === 0 ? '—' : `${partes.join(' + ')}${de}`;
}

/** Los grupos de «Precios», en el orden del prototipo; los productos retirados no salen; un grupo vacío, tampoco. */
export function gruposDelCatalogo(productos: readonly ProductoDelCatalogo[], reglas: readonly ReglaDeComision[], ahora: Date): GrupoCatalogo[] {
    const vivos = productos.filter((p) => p.status !== 'retired');
    const fila = (p: ProductoDelCatalogo): FilaCatalogo => ({ clave: p.id, nombre: p.name, linea: lineaProducto(p), precio: textoPrecio(p.price), producto: p, regla: null, programada: null });
    const grupo = (g: { titulo: string; nota: string; tipos: readonly ProductoDelCatalogo['kind'][] }): GrupoCatalogo => ({ titulo: g.titulo, nota: g.nota, filas: vivos.filter((p) => g.tipos.includes(p.kind)).map(fila) });
    const comision: GrupoCatalogo = {
        titulo: 'Comisión de plataforma',
        nota: 'La paga el comprador de cada entrada',
        filas: reglasVigentes(reglas, ahora)
            .filter((r) => r.payer === 'buyer')
            .map((r) => ({
                clave: r.id,
                nombre: NOMBRE_COMISION[r.concept],
                linea: lineaComision(r),
                precio: r.fixed === null || r.fixed === undefined ? '—' : euros(r.fixed.amount),
                producto: null,
                regla: r,
                programada: comisionProgramada(reglas, r, ahora),
            })),
    };
    return [...GRUPOS.map(grupo), comision, grupo(PAUSA)].filter((g) => g.filas.length > 0);
}

/** El precio que ya está programado para un producto (`next_price`, G1134) o, si no lo da la API, su precio si aún no vale. */
export function precioProgramado(p: ProductoDelCatalogo, ahora: Date): PrecioDelCatalogo | null {
    if (p.next_price !== null && p.next_price !== undefined) {
        return p.next_price;
    }
    return esProgramado(p.price, ahora) ? (p.price ?? null) : null;
}

/** La regla del mismo producto y concepto que empieza más adelante (la próxima), en texto para su chip. */
export function comisionProgramada(reglas: readonly ReglaDeComision[], vigente: ReglaDeComision, ahora: Date): string | null {
    const futuras = reglas
        .filter((r) => r.id !== vigente.id && r.product_code === vigente.product_code && r.concept === vigente.concept && r.payer === vigente.payer && Date.parse(r.valid_from) > ahora.getTime())
        .sort((a, b) => Date.parse(a.valid_from) - Date.parse(b.valid_from));
    const r = futuras[0];
    if (r === undefined) {
        return null;
    }
    const desde = new Date(r.valid_from);
    const partes = [r.fixed === null || r.fixed === undefined || r.fixed.amount === 0 ? null : euros(r.fixed.amount), r.percent_bp === 0 ? null : `${(r.percent_bp / 100).toLocaleString('es-ES')} %`].filter((x) => x !== null);
    return `${partes.length === 0 ? '0 %' : partes.join(' + ')} desde el ${String(enMadrid(desde).dia)} de ${MESES[enMadrid(desde).mes] ?? ''}`;
}

// ── Comisión de plataforma (G1136): ventana propia con importe fijo, porcentaje y motivo.

/** Textos de la ventana de la comisión. */
// Provisional hasta C.63.
export function textoVentanaComision(nombre: string, r: ReglaDeComision): { titulo: string; texto: string } {
    return {
        titulo: `Cambiar la comisión de ${nombre}`,
        texto: `Ahora es ${lineaComision(r)}. La comisión nueva vale desde la fecha que elijas (no puede ser pasada) y cierra la actual ese día. Queda en Auditoría con el motivo.`,
    };
}

/** «1», «1,50» → céntimos; vacío, 0; null si no es un importe válido (no negativo, hasta 2 decimales). */
export function centimosFijos(texto: string): number | null {
    const t = texto.trim().replace(',', '.');
    if (t === '') {
        return 0;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(t)) {
        return null;
    }
    return Math.round(Number.parseFloat(t) * 100);
}

/** «1,5» → 150 puntos básicos; vacío, 0; null si no está entre 0 y 100 con hasta 2 decimales. */
export function puntosBasicosDe(texto: string): number | null {
    const t = texto.trim().replace(',', '.');
    if (t === '') {
        return 0;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(t)) {
        return null;
    }
    const v = Math.round(Number.parseFloat(t) * 100);
    return v <= 10_000 ? v : null;
}

export const ERROR_FIJO = 'Escribe el importe, por ejemplo 1 o 0,50.';
export const ERROR_PORCENTAJE = 'Entre 0 y 100, por ejemplo 1,5.';

/** Para `createPlatformFeeRule`: el mismo producto, concepto y quién paga que la regla vigente. */
export function nuevaComision(r: ReglaDeComision, centimos: number, puntos: number, desde: Date, motivo: string): NuevaReglaDeComision {
    return {
        product_code: r.product_code,
        concept: r.concept,
        fixed: { amount: centimos, currency: r.fixed?.currency ?? 'EUR' },
        percent_bp: puntos,
        payer: r.payer,
        valid_from: desde.toISOString(),
        reason: motivo.trim(),
    };
}

/** Lo que cuesta ahora, para el texto de la ventana («Ahora cuesta 49 €.»). */
export function textoVentanaPrecio(p: ProductoDelCatalogo): { titulo: string; texto: string } {
    const ahora = p.price === null || p.price === undefined ? null : euros(p.price.unit.amount);
    return {
        titulo: `Cambiar el precio de ${p.name}`,
        texto: `${ahora === null ? '' : `Ahora cuesta ${ahora}. `}El precio nuevo vale para los contratos nuevos desde la fecha que elijas; quien ya lo tiene sigue pagando lo mismo.`,
    };
}

/** «42», «42,50» → céntimos; null si no es un precio mayor que cero. */
export function centimosDe(texto: string): number | null {
    const v = Number.parseFloat(texto.trim().replace(',', '.'));
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
}

export const ERROR_PRECIO = 'Escribe el precio, por ejemplo 42.';

/** El precio nuevo, programado desde el día 1 elegido y **solo para contratos nuevos** (G185). */
export function nuevoPrecio(p: ProductoDelCatalogo, centimos: number, desde: Date): NuevoPrecio {
    const interval = p.price?.interval ?? (p.kind === 'one_time' || p.kind === 'topup' ? 'one_time' : 'month');
    return { unit: { amount: centimos, currency: p.price?.unit.currency ?? 'EUR' }, interval, effective_from: desde.toISOString(), applies_to_existing: false };
}

// ── Cupones

const fechaCorta = (d: Date) => `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${String(d.getFullYear())}`;

export function cuponCaducado(c: CuponDelCatalogo, ahora: Date): boolean {
    return typeof c.valid_until === 'string' && Date.parse(c.valid_until) < ahora.getTime();
}

/** «Base · 6 meses · hasta el 31/12/2026» (o «caducó el …»), con los nombres de los productos del catálogo. */
export function lineaCupon(c: CuponDelCatalogo, nombres: Readonly<Record<string, string>>, ahora: Date): string {
    const productos = c.applies_to.map((codigo) => nombres[codigo] ?? codigo).join(', ');
    const meses = c.duration_months === null || c.duration_months === undefined ? 'sin límite' : `${String(c.duration_months)} meses`;
    const hasta = typeof c.valid_until === 'string' ? `${cuponCaducado(c, ahora) ? ' · caducó el ' : ' · hasta el '}${fechaCorta(new Date(c.valid_until))}` : '';
    return `${productos} · ${meses}${hasta}`;
}

/** Productos a los que se puede aplicar un cupón (prototipo `PROD_NAMES`: la base y los bloques). */
export function productosParaCupon(productos: readonly ProductoDelCatalogo[]): { codigo: string; nombre: string }[] {
    return productos.filter((p) => p.status !== 'retired' && (p.kind === 'base' || p.kind === 'block' || p.kind === 'quantity')).map((p) => ({ codigo: p.code, nombre: p.name }));
}

export const SIN_FECHA = 'Sin fecha límite';

/** «Se puede aplicar hasta»: sin fecha o el último día de este trimestre y de los dos siguientes. */
export function fechasHasta(ahora: Date): { valor: string; texto: string }[] {
    const trimestre = Math.floor(ahora.getMonth() / 3);
    const fines = [0, 1, 2].map((n) => new Date(ahora.getFullYear(), (trimestre + n + 1) * 3, 0));
    return [{ valor: '', texto: SIN_FECHA }, ...fines.map((d) => ({ valor: `${String(d.getFullYear())}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`, texto: fechaCorta(d) }))];
}

export interface FormularioCupon {
    codigo: string;
    nombre: string;
    porcentaje: string;
    meses: string;
    /** `aaaa-mm-dd` o vacío (sin fecha límite). */
    hasta: string;
    productos: string[];
}

export const CUPON_VACIO: FormularioCupon = { codigo: '', nombre: '', porcentaje: '', meses: '', hasta: '', productos: ['base'] };

export interface ErroresCupon {
    codigo: string | null;
    nombre: string | null;
    porcentaje: string | null;
    productos: string | null;
}

/** Validación del prototipo (`cErr`); el código no puede repetir uno que ya hay. */
export function erroresCupon(f: FormularioCupon, existentes: readonly CuponDelCatalogo[]): ErroresCupon {
    const pct = Number(f.porcentaje);
    return {
        codigo: !/^[a-z0-9_]{3,40}$/.test(f.codigo)
            ? f.codigo === ''
                ? 'Escribe el código.'
                : 'Solo minúsculas, números y _, de 3 a 40.'
            : existentes.some((c) => c.code === f.codigo)
              ? 'Ya hay un cupón con ese código.'
              : null,
        nombre: f.nombre.trim() === '' ? 'Escribe el nombre.' : null,
        porcentaje: f.porcentaje.trim() !== '' && Number.isInteger(pct) && pct >= 1 && pct <= 100 ? null : 'Entre 1 y 100.',
        productos: f.productos.length === 0 ? 'Elige al menos un producto.' : null,
    };
}

export function hayErrores(e: ErroresCupon): boolean {
    return Object.values(e).some((x) => x !== null);
}

/** El cupón para `createCatalogCoupon`; «hasta» vale hasta el final de ese día. */
export function nuevoCupon(f: FormularioCupon): NuevoCupon {
    const meses = f.meses.trim();
    const [a, m, d] = f.hasta.split('-').map(Number);
    return {
        code: f.codigo,
        name: f.nombre.trim(),
        percent_off: Number(f.porcentaje),
        applies_to: f.productos,
        // «Meses que dura» solo admite cifras (vacío o 0: sin límite).
        duration_months: meses === '' || Number(meses) === 0 ? null : Number(meses),
        ...(f.hasta === '' || a === undefined || m === undefined || d === undefined ? {} : { valid_until: new Date(a, m - 1, d, 23, 59, 59).toISOString() }),
    };
}
